import type { TiendaPorHora } from '../types/indicadores.types';
import { fmtInt } from './format';

/** Un punto plano `{x,y}` en coordenadas del `viewBox` del SVG (0 0 760 250). */
export interface ChartPoint {
  x: number;
  y: number;
}

/** Una gridline horizontal + su label del eje Y. */
export interface ChartGridLine {
  y: number;
  ty: number;
  label: string;
}

/** Un tick del eje X (una hora de `porHora`). */
export interface ChartXTick {
  x: number;
  label: string;
}

/**
 * Geometría completa del SVG "Comportamiento de hoy · por hora" (líneas
 * 202-212 y 419-435 del handoff), con 4 series:
 * - `areaPath`/`consPath`: zona sombreada + línea de **Consultas** (gris).
 * - `atendPath`/`dots`: línea + puntos de **Atendidas** (verde).
 * - `negadosPath`/`negadosDots`: línea de **Negados** (rojo) — mismo eje
 *   primario que Consultas/Atendidas (son conteos comparables).
 * - `ventaPath`/`ventaDots`: línea de **Venta (pares)** (azul) — eje **Y
 *   secundario** propio (`gridY2`/`maxVenta`), porque pares no es un conteo
 *   comparable a consultas/negados/atendidas.
 *
 * Las 4 series comparten el mismo recorte por `nowHour` (solo se grafica el
 * pasado, `pastP`) y el mismo eje X (`xticks`).
 */
export interface ChartGeometry {
  areaPath: string;
  consPath: string;
  atendPath: string;
  dots: ChartPoint[];
  /** Línea de Negados (rojo), eje primario — igual recorte por `pastP` que `consPath`/`atendPath`. */
  negadosPath: string;
  /** Puntos de Negados, por si la UI quiere marcarlos igual que `dots` de Atendidas. */
  negadosDots: ChartPoint[];
  gridY: ChartGridLine[];
  xticks: ChartXTick[];
  nowX: number;
  futureX: number;
  futureW: number;
  /** Tope del eje Y PRIMARIO (Consultas/Atendidas/Negados). */
  maxY: number;
  /**
   * Línea de Venta (pares), eje SECUNDARIO. Puede traer "huecos" (varios
   * subpaths, comandos `M` intermedios) cuando alguna hora pasada tiene
   * `venta: null` (Proscai caído) — esa hora simplemente no se grafica, sin
   * interpolar ni romper el resto de la serie.
   */
  ventaPath: string;
  /** Un punto por cada hora pasada con `venta` no-null (las `null` se omiten). */
  ventaDots: ChartPoint[];
  /** Gridlines/labels del eje Y SECUNDARIO (0..`maxVenta`), mismo layout que `gridY`. */
  gridY2: ChartGridLine[];
  /** Tope del eje Y SECUNDARIO (Venta), calculado sobre las horas YA PASADAS. */
  maxVenta: number;
}

/** Dimensiones del `viewBox="0 0 760 250"` (línea 202 del handoff). */
const VIEWBOX_W = 760;
const PAD_L = 34;
const PAD_R = 16;
const PAD_T = 16;
const PAD_B = 28;
const PLOT_H = 250 - PAD_T - PAD_B;
const PLOT_W = VIEWBOX_W - PAD_L - PAD_R;
const RIGHT_EDGE = PAD_L + PLOT_W;

/**
 * Índice (0-based) de la última hora de `porHora` que ya "pasó" (hora <=
 * `nowHour`), asumiendo `porHora` ordenado ascendente por `hora` (10..21,
 * como lo entrega el backend). `-1` si NINGUNA hora pasó todavía (`nowHour`
 * menor que la primera hora de la serie, ej. antes de las 10:00 — brief:
 * "clamp: all future if <10"). Si `nowHour` es mayor que la última hora de la
 * serie (después de las 21:00), devuelve el último índice — TODA la serie es
 * pasado ("clamp: all past if >21").
 */
export function computeNowIndex(porHora: TiendaPorHora[], nowHour: number): number {
  let idx = -1;
  for (let i = 0; i < porHora.length; i += 1) {
    if (porHora[i].hora <= nowHour) {
      idx = i;
    } else {
      break;
    }
  }
  return idx;
}

/**
 * Hora actual (0..23) en la zona horaria `America/Mexico_City`, sin importar
 * en qué huso corra el navegador/dispositivo — el "ahora" del gráfico SIEMPRE
 * se calcula en hora de México (brief: la tienda opera en MX aunque el
 * servidor/browser esté en otro huso).
 */
export function getNowHourMx(date: Date = new Date()): number {
  const hourStr = date.toLocaleString('en-US', {
    timeZone: 'America/Mexico_City',
    hour: '2-digit',
    hour12: false,
  });
  const parsed = parseInt(hourStr, 10);
  // Algunas implementaciones de Intl devuelven "24" para la medianoche con
  // hour12:false (en vez de "00") — se normaliza a 0.
  return parsed === 24 ? 0 : parsed;
}

/**
 * Construye toda la geometría SVG del gráfico por hora (paths, gridlines,
 * ticks, marcador "ahora" y rect futuro) a partir de `porHora` + la hora MX
 * actual. Recreado pixel-exact de las líneas 419-435 del handoff, con las 4
 * series descritas en `ChartGeometry` (Consultas/Atendidas/Negados en el eje
 * primario, Venta en pares en un eje secundario propio).
 *
 * Solo se grafican las horas <= `nowHour` (`pastP` en el handoff): las horas
 * futuras quedan sin trazo y cubiertas por el rect sombreado
 * (`futureX`/`futureW`) — igual que el prototipo, que corta las líneas en
 * `D.nowIdx`. Las 4 series comparten ese mismo recorte.
 */
export function buildChartGeometry(porHora: TiendaPorHora[], nowHour: number): ChartGeometry {
  const n = porHora.length;
  const X = (i: number) => PAD_L + (n > 1 ? (i * PLOT_W) / (n - 1) : 0);

  // Eje primario (conteos): Consultas y Negados comparten escala — el tope se
  // calcula sobre el máximo de AMBAS series (no solo Consultas), para que
  // Negados nunca se salga del plot aunque en algún punto supere a Consultas.
  // Igual que antes, se calcula sobre TODA la serie (no solo el pasado), para
  // que el eje no "salte" de escala según avanza `nowHour`.
  const maxRaw =
    n > 0 ? Math.max(...porHora.map((p) => Math.max(p.consultas, p.negados))) : 0;
  const maxY = Math.max(10, Math.ceil((maxRaw * 1.18) / 5) * 5);
  const Y = (v: number) => PAD_T + PLOT_H * (1 - v / maxY);
  const baseY = Y(0);

  const nowIdx = computeNowIndex(porHora, nowHour);
  const pastCount = Math.max(nowIdx + 1, 0);
  const pastP = porHora.slice(0, pastCount);

  // Índice usado SOLO para ubicar el marcador "ahora"/rect futuro: si
  // `nowIdx` es -1 (aún no llega la primera hora de la serie), se ancla al
  // borde izquierdo (X(0)) para sombrear TODO el plot como futuro.
  const xIdx = Math.max(nowIdx, 0);
  const nowX = X(xIdx);
  const futureX = nowX;
  const futureW = RIGHT_EDGE - nowX;

  const areaPath =
    pastP.length === 0
      ? ''
      : `M ${X(0)} ${baseY} ` +
        pastP.map((p, i) => `L ${X(i)} ${Y(p.consultas)}`).join(' ') +
        ` L ${X(pastP.length - 1)} ${baseY} Z`;

  const consPath =
    pastP.length === 0 ? '' : 'M ' + pastP.map((p, i) => `${X(i)} ${Y(p.consultas)}`).join(' L ');

  const atendPath =
    pastP.length === 0 ? '' : 'M ' + pastP.map((p, i) => `${X(i)} ${Y(p.atendidas)}`).join(' L ');

  const dots: ChartPoint[] = pastP.map((p, i) => ({ x: X(i), y: Y(p.atendidas) }));

  const negadosPath =
    pastP.length === 0 ? '' : 'M ' + pastP.map((p, i) => `${X(i)} ${Y(p.negados)}`).join(' L ');

  const negadosDots: ChartPoint[] = pastP.map((p, i) => ({ x: X(i), y: Y(p.negados) }));

  const gridY: ChartGridLine[] = [0, 0.25, 0.5, 0.75, 1].map((f) => {
    const y = Y(maxY * f);
    return { y, ty: y - 4, label: fmtInt(maxY * f) };
  });

  const xticks: ChartXTick[] = porHora.map((p, i) => ({
    x: X(i),
    label: String(p.hora).padStart(2, '0'),
  }));

  // Eje secundario (Venta en pares): escala INDEPENDIENTE del eje primario,
  // porque pares no es un conteo comparable a consultas/negados/atendidas.
  // El tope se calcula solo sobre las horas YA PASADAS (`pastP`) — a
  // diferencia del eje primario, la venta de horas futuras todavía no existe
  // (no llega en 0 como consultas/negados), así que no tiene sentido incluir
  // horas futuras en el cálculo del máximo.
  const ventaPastVals = pastP
    .map((p) => p.venta)
    .filter((v): v is number => v !== null && v !== undefined);
  const maxVentaRaw = ventaPastVals.length > 0 ? Math.max(...ventaPastVals) : 0;
  const maxVenta = Math.max(10, Math.ceil((maxVentaRaw * 1.18) / 5) * 5);
  const Y2 = (v: number) => PAD_T + PLOT_H * (1 - v / maxVenta);

  // La venta puede llegar `null` por hora (Proscai caído/timeout): se corta
  // el trazo en cada hueco con un nuevo comando `M` (subpath), en vez de
  // interpolar sobre el hueco o tronar. Las horas con `null` simplemente no
  // aportan punto ni tramo de línea.
  let ventaPath = '';
  const ventaDots: ChartPoint[] = [];
  let ventaTrazoAbierto = false;
  pastP.forEach((p, i) => {
    if (p.venta === null || p.venta === undefined) {
      ventaTrazoAbierto = false;
      return;
    }
    const x = X(i);
    const y = Y2(p.venta);
    ventaDots.push({ x, y });
    ventaPath += (ventaPath ? ' ' : '') + `${ventaTrazoAbierto ? 'L' : 'M'} ${x} ${y}`;
    ventaTrazoAbierto = true;
  });

  const gridY2: ChartGridLine[] = [0, 0.25, 0.5, 0.75, 1].map((f) => {
    const y = Y2(maxVenta * f);
    return { y, ty: y - 4, label: fmtInt(maxVenta * f) };
  });

  return {
    areaPath,
    consPath,
    atendPath,
    dots,
    negadosPath,
    negadosDots,
    gridY,
    xticks,
    nowX,
    futureX,
    futureW,
    maxY,
    ventaPath,
    ventaDots,
    gridY2,
    maxVenta,
  };
}
